/**
 * Phase 28B — Browser-native screen + mic + tab-audio recording.
 *
 * Used by Creator Studio subscribers to record the OutboundCallsModal screen
 * along with the live-listen audio that's already being played to their
 * speakers. The recorded blob is exported as WebM (browser-native, lossless).
 *
 * Usage:
 *   const rec = new ScreenRecorder();
 *   await rec.start();
 *   // ... user calls / chats / does whatever ...
 *   const blob = await rec.stop();
 *   const url = URL.createObjectURL(blob);
 *
 * Notes
 * - getDisplayMedia must be triggered from a user gesture (button click).
 * - On Chrome/Edge it can include tab/system audio if the user picks "Share tab"
 *   and ticks "Share audio". Safari is screen-only (no audio yet).
 */
export class ScreenRecorder {
  constructor() {
    this.stream = null;
    this.recorder = null;
    this.chunks = [];
    this.state = "idle"; // idle | recording | stopped | error
  }

  async start({ withMic = false } = {}) {
    if (this.state === "recording") return;
    try {
      // Capture screen + (optional) system/tab audio
      const display = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 30, max: 60 } },
        audio: true,
      });
      let mixedStream = display;
      if (withMic && navigator.mediaDevices.getUserMedia) {
        try {
          const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
          // Combine display + mic into one stream
          const tracks = [
            ...display.getVideoTracks(),
            ...display.getAudioTracks(),
            ...mic.getAudioTracks(),
          ];
          mixedStream = new MediaStream(tracks);
        } catch (micErr) {
          // Mic denied — keep going with screen audio only
          console.warn("Mic capture failed:", micErr);
        }
      }
      this.stream = mixedStream;

      // Choose the best codec available
      const mime =
        MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus") ? "video/webm;codecs=vp9,opus" :
        MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus") ? "video/webm;codecs=vp8,opus" :
        "video/webm";

      this.recorder = new MediaRecorder(this.stream, { mimeType: mime, videoBitsPerSecond: 4_000_000 });
      this.chunks = [];
      this.recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) this.chunks.push(e.data);
      };
      // User stopping the share via browser UI → auto-stop us too
      display.getVideoTracks()[0].addEventListener("ended", () => {
        if (this.state === "recording") this.stop();
      });
      this.recorder.start(1000); // gather chunks every second
      this.state = "recording";
    } catch (e) {
      this.state = "error";
      throw e;
    }
  }

  stop() {
    return new Promise((resolve, reject) => {
      if (!this.recorder || this.state !== "recording") {
        resolve(null);
        return;
      }
      this.recorder.onstop = () => {
        const blob = new Blob(this.chunks, { type: this.recorder.mimeType });
        try {
          this.stream.getTracks().forEach((t) => t.stop());
        } catch (_) { /* ignore */ }
        this.stream = null;
        this.recorder = null;
        this.chunks = [];
        this.state = "stopped";
        resolve(blob);
      };
      this.recorder.onerror = (e) => reject(e);
      this.recorder.stop();
    });
  }
}
